import type { CountryConfig } from '~/nuxt.config';

export const useHrefLang = () => {
  const { $config, $i18n, runWithContext } = useNuxtApp();
  const route = useRoute();
  const mainCountryCodes = Object.values($config.public.markets as unknown as CountryConfig[]).filter(Boolean);

  const setHrefLangForProduct = (hrefLang: any) => runWithContext(() => {
    const links = mainCountryCodes
      .filter(({ locales }) => hrefLang[locales[0]])
      .map(({ host, locales }) => ({
        rel: 'alternate',
        key: `hreflang-${locales[0]}`,
        href: `https://${host}/p/${hrefLang[locales[0]]}/${route.params.id}`,
        hreflang: locales[0],
      }));
    useHead({ link: links });

    return links;
  });

  const setHrefLangForCategory = (slug1: Record<string, string>, slug2?: Record<string, string>, isSale?: boolean, isNew?: boolean) => runWithContext(() => {
    const getHref = (host: string, locale: string): string => {
      let mainCategory = 'c';
      if (isSale) {
        mainCategory = `c/s/${$i18n.t('Sale').toLowerCase()}`;
      }
      if (isNew) {
        mainCategory = `c/n/${$i18n.t('New').toLowerCase()}`;
      }
      if ((isSale || isNew) && slug1[locale] === 'searchresult') {
        return `https://${host}/${mainCategory}`;
      }
      if (!slug1) {
        return `https://${host}/${mainCategory}`;
      }
      if (slug2) {
        return `https://${host}/${mainCategory}/${slug1[locale]}/${slug2[locale]}`;
      }
      return `https://${host}/${mainCategory}/${slug1[locale]}`;
    };

    const links = mainCountryCodes
      .filter(({ locales }) => slug1[locales[0]])
      .map(({ host, locales }) => ({
        rel: 'alternate',
        key: `hreflang-${locales[0]}`,
        href: getHref(host, locales[0]),
        hreflang: locales[0],
      }));
    useHead({ link: links });
  });

  const setHrefLangForMagnolia = (hrefLang: any) => runWithContext(() => {
    if (!hrefLang) {
      return [];
    }

    const links = hrefLang['@nodes']?.map((node: any) => {
      if (hrefLang[node]?.page?.['@path']) {
        const [, country, ...rest] = hrefLang[node].page['@path'].replace('/Home', '').split('/');
        const countryCodeConfig = mainCountryCodes.find(({ countryCode }) => countryCode === country);

        if (!countryCodeConfig) {
          return;
        }

        return {
          rel: 'alternate',
          key: `hreflang-${countryCodeConfig.locales[0]}`,
          href: `https://${[countryCodeConfig.host, ...rest].filter(Boolean).join('/')}`,
          hreflang: countryCodeConfig.locales[0],
        };
      }
    });

    if (hrefLang['@path']) {
      const [, currentCountry, ...currentRest] = hrefLang['@path'].replace('/Home', '').replace('/hrefLang', '').split('/');
      const currentCountryCodeConfig = mainCountryCodes.find(({ countryCode }) => countryCode === currentCountry);

      links.push({
        rel: 'alternate',
        key: `hreflang-${currentCountryCodeConfig?.locales[0]}`,
        href: `https://${[currentCountryCodeConfig?.host, ...currentRest].filter(Boolean).join('/')}`,
        hreflang: currentCountryCodeConfig?.locales[0],
      });
    }

    if (links) {
      useHead({ link: links.filter(Boolean) });
    }
  });

  return {
    setHrefLangForProduct,
    setHrefLangForCategory,
    setHrefLangForMagnolia,
  };
};
